import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
  getInvitationsFilter,
  getSearchFilter,
  getUsersFilter,
} from './getFilters';
import TeammatesFilter from './TeammatesFilter';
import TeammatesRow from './TeammatesRow';
import * as styles from './TeammatesView.module.scss';
import useTeammatesFilter from './useTeammatesFilter';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Card, { Cards } from '../../components/Card';
import PopOver from '../../components/deprecated/PopOver';
import EmptyState from '../../components/EmptyState';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import SearchInput from '../../components/SearchInput';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import filterIcon from '../../icons/non-prefilled/16x16/filter.svg';
import { InviteButton } from '../../organization/InviteButton';
import SettingsContainerView from '../../settings/SettingsContainerView';
import { UIActions } from '../../ui/uiSlice';
import nonEmpty from '../../utils/nonEmpty';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';

const TEAMMATES_USER_FRAGMENT = gql(`
 fragment TeammatesUser on User {
    id
    ...TeammatesRow_user
  }
`);

type TeammatesUserFragment = DocumentType<typeof TEAMMATES_USER_FRAGMENT>;

const TEAMMATES_INVITATION_FRAGMENT = gql(`
 fragment TeammatesInvitation on Invitation {
    id
    ...TeammatesRow_invitation
  }
`);

type TeammatesInvitationFragment = DocumentType<
  typeof TEAMMATES_INVITATION_FRAGMENT
>;

const TEAMMATES_QUERY = gql(/** @complexity 23 */ `
  query Teammates {
    session: currentSession {
      organization {
        id
        users(includeDeactivated: true) {
          ...TeammatesUser
        }
        invitations {
          ...TeammatesInvitation
        }
        settings {
          requireInviteApproval
        }
      }
    }
  }
`);

const LIMIT_STEP = 30;

interface TeammatesProps {
  search?: string;
}

const Teammates = ({ search }: TeammatesProps) => {
  const { data } = useQuery(TEAMMATES_QUERY);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [limit, setLimit] = useState<number>(LIMIT_STEP);

  const isRequiringInviteApproval =
    data?.session?.organization?.settings?.requireInviteApproval ?? false;

  const isMobile = useIsSize(Sizes.SM);
  const dispatch = useAppDispatch();

  const { types, statuses, reset } = useTeammatesFilter();

  const [searchQuery, setSearchQuery] = useState<string>(search ?? '');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 200);

  const { teammates, searchedTeammatesCount, allTeammatesCount } = useMemo<{
    teammates: (TeammatesUserFragment | TeammatesInvitationFragment)[];
    searchedTeammatesCount: number;
    allTeammatesCount: number;
  }>(() => {
    const users = data?.session?.organization?.users ?? [];
    const invitations = data?.session?.organization?.invitations ?? [];

    const searchedUsers = users.filter(getSearchFilter(debouncedSearchQuery));

    const filteredUsers = searchedUsers.filter(getUsersFilter(types, statuses));

    const searchedInvitations = invitations.filter(
      getSearchFilter(debouncedSearchQuery),
    );

    const filteredInvitations = searchedInvitations.filter(
      getInvitationsFilter(types, statuses),
    );

    const allUsers = [...filteredUsers, ...filteredInvitations].filter(
      nonEmpty,
    );

    return {
      teammates: allUsers,
      searchedTeammatesCount: debouncedSearchQuery
        ? searchedUsers.length + searchedInvitations.length
        : 0,
      allTeammatesCount: users.length + invitations.length,
    };
  }, [
    data?.session?.organization?.invitations,
    data?.session?.organization?.users,
    types,
    statuses,
    debouncedSearchQuery,
  ]);

  useEffect(() => {
    setLimit(LIMIT_STEP);
  }, [teammates.length]);

  useEffect(() => {
    if (isMobile && isFilterOpen) {
      dispatch(
        UIActions.openModal(TeammatesFilter, {
          asModal: true,
          filteredTeammates: teammates.length,
          requireInviteApproval: isRequiringInviteApproval,
          close: () => {
            setIsFilterOpen(false);
            dispatch(UIActions.closeModal());
          },
        }),
      );
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isMobile, isFilterOpen, teammates.length, types, statuses]);

  const numberOfFilters = types.length + statuses.length;

  return (
    <SettingsContainerView
      title="Teammates"
      titleRight={<InviteButton />}
      className="admin-members-view"
      subtitle={
        <div className={styles.subtitleWrapper}>
          <h3 className={styles.subtitle}>{teammates.length} total</h3>
          <div className={styles.subtitleSearch}>
            <PopOver
              placement="bottom-end"
              trigger="click"
              preventCloseOnContentClick
              popOver={
                <PopOver.Container noPadding width="auto">
                  <TeammatesFilter
                    filteredTeammates={teammates.length}
                    requireInviteApproval={isRequiringInviteApproval}
                  />
                </PopOver.Container>
              }
              open={!isMobile && isFilterOpen}
              onOpenChange={(open: boolean) => {
                if (!isMobile) {
                  setIsFilterOpen(open);
                }
              }}
              popOverPortal={document.body}
            >
              <Button
                className={styles.filterButton}
                variant="secondary"
                onClick={() => {
                  setIsFilterOpen(true);
                }}
                icon={filterIcon}
              >
                Filter{numberOfFilters > 0 ? ` (${numberOfFilters})` : ''}
              </Button>
            </PopOver>
            <div className={styles.searchWrapper}>
              <SearchInput
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                value={searchQuery}
                placeholder="Search"
              />
            </div>
          </div>
        </div>
      }
    >
      <Cards>
        {teammates.length > 0 && (
          <>
            {teammates.slice(0, limit).map((teammate) => (
              <Card key={teammate.id} noPadding>
                {teammate.__typename === 'User' && (
                  <TeammatesRow user={teammate} />
                )}
                {teammate.__typename === 'Invitation' && (
                  <TeammatesRow invitation={teammate} />
                )}
              </Card>
            ))}
            {teammates.length < searchedTeammatesCount && (
              <div className={styles.showAllResultsText}>
                Some results exist outside of your filtering criteria.{' '}
                <Button onClick={reset} variant="link">
                  Show all {searchedTeammatesCount} results
                </Button>
              </div>
            )}
          </>
        )}
        {teammates.length === 0 && (
          <div className={styles.emptyContainer}>
            <EmptyState
              imageName="notFound"
              title="No results found"
              message="Some results exist outside of your filtering criteria."
              action={
                <Button onClick={reset} variant="secondary">
                  Show all{' '}
                  {searchedTeammatesCount > 0
                    ? searchedTeammatesCount
                    : allTeammatesCount}{' '}
                  results
                </Button>
              }
            />
          </div>
        )}

        <InfiniteScrollWaypoint
          fetchMore={
            limit >= teammates.length
              ? null
              : () => setLimit(limit + LIMIT_STEP)
          }
        />
      </Cards>
    </SettingsContainerView>
  );
};

export default Teammates;
